import { useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Flex,
  Form,
  OptionCard,
  OptionCardGroup,
  PanelHeader,
  PanelSection,
  PanelTitle,
  Paragraph,
} from '@customerio/pluma-components/react';
import { useSignupFlow } from '../signup-flow';
import { onboardingPersonalizeSubmittedProps, SignupAnalyticsEvent, signupTypeProps } from '../utils/analytics';
import { CHANNEL_OPTIONS } from './channel-options';

const HEADING = 'Which channels do you plan on using?';

export function ChannelsStep() {
  const {
    messageChannels,
    setMessageChannels,
    effectiveSignupType,
    resolvedSignupType,
    isCompletingSignup,
    goToNextStep,
    trackSignupEvent,
  } = useSignupFlow();

  // Fire once per step entry — the analog of Ember's fire-on-route-transition
  // analytics; the ref keeps StrictMode's double-invoked effects from
  // double-counting the funnel.
  const hasTrackedView = useRef(false);
  useEffect(() => {
    if (hasTrackedView.current) {
      return;
    }
    hasTrackedView.current = true;
    trackSignupEvent(SignupAnalyticsEvent.ChannelsViewed, signupTypeProps({ signupType: effectiveSignupType }));
  }, [trackSignupEvent, effectiveSignupType]);

  const onSubmit = async () => {
    trackSignupEvent(
      SignupAnalyticsEvent.ChannelsSubmitted,
      // `commsOptIn` is deliberately omitted and the email volume is always
      // null: this step collects neither, so both keys drop off the wire.
      onboardingPersonalizeSubmittedProps({
        signupType: effectiveSignupType,
        channels: messageChannels,
        estimatedEmailVolume: null,
        resolvedSignupType,
      }),
    );
    await goToNextStep();
  };

  return (
    <Box data-testid="signup-step-channels">
      <PanelHeader>
        <PanelTitle level="1">{HEADING}</PanelTitle>
      </PanelHeader>
      <PanelSection>
        <Form onSubmit={onSubmit}>
          <Paragraph variant="secondary" mb="200">
            Select all that apply.
          </Paragraph>

          <OptionCardGroup
            ariaLabel={HEADING}
            inputType="checkbox"
            cardsLayout="grid"
            cardsColumns={3}
            cardsGap="150"
            value={messageChannels}
            onChange={(values: string[]) => setMessageChannels(values)}
          >
            {CHANNEL_OPTIONS.map((option) => (
              <OptionCard
                key={option.value}
                value={option.value}
                title={option.label}
                ariaLabel={option.label}
                icon={option.icon}
                layout="vertical"
                w="full"
              />
            ))}
          </OptionCardGroup>

          <Flex justifyContent="flex-end" mt="300">
            <Button
              type="submit"
              variant="primary"
              icon="chevron-right"
              iconPosition="trailing"
              isDisabled={messageChannels.length === 0}
              isLoading={isCompletingSignup}
            >
              Continue
            </Button>
          </Flex>
        </Form>
      </PanelSection>
    </Box>
  );
}
